import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import Button from '../Components/ui/Button';
import Logo from '../Components/ui/Logo';
import { useAuth } from '../context/AuthContext';

const ROLE_DASHBOARD = {
  client: '/dashboard',
  therapist: '/therapist',
  venue: '/venue',
  admin: '/admin'
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: "Massage Therapy", path: "../massage-therapy" }
  ];

  return (
    <nav className={`fixed w-full h-1/8 z-50 top-0 left-0 transition-all duration-300 ${
      isScrolled ? 'bg-white border-b border-gray-200' : 'bg-transparent'
    }`}>
      <div className="max-w-screen-xl flex flex-wrap items-center justify-between md:justify-around mx-auto p-4">
        {/* Site Logo */}
        <Link to="../" className="flex items-center">
          <Logo variant={isScrolled ? 'dark' : 'light'} />
        </Link>
        
        {/* Mobile View Menu Icon */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden inline-flex text-dim bg-secondary rounded-full drop-shadow-sm items-center p-2 text-dim hover:bg-secondary-light hover:text-dim-dark rounded-lg"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <div className={`${isOpen ? 'block' : 'hidden'} bg-gray-100 w-full rounded-b-2xl md:bg-transparent md:flex md:items-center md:w-auto`}>
          <ul className={`font-medium flex flex-col md:flex-row md:items-center md:space-x-12 md:space-y-0 space-y-4 mt-4 md:mt-0 ${
            isScrolled ? 'md:bg-transparent' : 'md:bg-secondary md:rounded-full'
          } p-4 md:py-2 md:px-6`}>
            <li>
              <Link to="../" className="block text-gray-900 hover:text-accent-hard">
                Home
              </Link>
            </li>
            <li>
              <Link to="../about" className="block text-gray-900 hover:text-accent-hard">
                About
              </Link>
            </li>
            <li className="relative">
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="flex items-center w-full text-gray-900 hover:text-accent-hard"
              >
                Services
                <svg 
                  className={`w-4 h-4 ml-1 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isServicesOpen && (
                <div className="md:absolute relative left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2">
                  {services.map((service, index) => (
                    <Link 
                      key={index}
                      to={service.path}
                      className="block px-4 py-2 text-gray-800 hover:bg-accent-lighter hover:text-accent-hard"
                      onClick={() => {
                        setIsServicesOpen(false);
                        setIsOpen(false);
                      }}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </li>
            <li>
              <Link to="../faq" className="block text-gray-900 hover:text-accent-hard">
                FAQ
              </Link>
            </li>
            <li className="md:hidden">
              {user ? (
                <div className="flex flex-col gap-3">
                  <Button to={ROLE_DASHBOARD[user.role] || '/'} className="w-full" onClick={() => setIsOpen(false)}>
                    MY ACCOUNT
                  </Button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 w-full text-gray-900 hover:text-accent-hard"
                  >
                    <LogOut size={16} /> Log Out
                  </button>
                </div>
              ) : (
                <Button
                  to="../login"
                  variant="primary"
                  className="w-full mb-2"
                  onClick={() => setIsOpen(false)}
                >
                  LOG IN
                </Button>
              )}
            </li>
            <li className="md:hidden">
              <Button to="../book-massage" className="w-full">
                BOOK APPOINTMENT
              </Button>
            </li>
          </ul>
        </div>

        <div className="hidden md:flex md:items-center md:gap-4">
          {user ? (
            <>
              <Link
                to={ROLE_DASHBOARD[user.role] || '/'}
                className="flex items-center gap-1.5 text-gray-900 hover:text-accent-hard"
              >
                <User size={18} /> My Account
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-gray-900 hover:text-accent-hard"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <Button to="../login" variant="primary">
              LOG IN
            </Button>
          )}
          <Button to="../book-massage">
            BOOK APPOINTMENT
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;